import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronRight } from 'lucide-react';
import { setItemStage } from '../../api/resources';
import { useToast } from '../../context/ToastContext';
import { fmtDate } from '../../utils/format';

// Kept in step with the server's own list (lib/productionStage.js).
export const STAGE_LABEL = {
  RECEIVED: 'Order received',
  CUTTING: 'Fabric cutting',
  CUTTING_DONE: 'Cutting done',
  ASSIGNED: 'Assigned to worker',
  STITCHING: 'Stitching',
  QC: 'QC / checking',
  ALTERATION: 'Alteration',
  READY: 'Ready',
  DELIVERED: 'Delivered',
};
export const STAGE_TONE = {
  RECEIVED: 'bg-surface-sunken text-content-muted',
  CUTTING: 'bg-info-50 text-info-700',
  CUTTING_DONE: 'bg-info-50 text-info-700',
  ASSIGNED: 'bg-brand-50 text-brand-700',
  STITCHING: 'bg-brand-50 text-brand-700',
  QC: 'bg-warning-50 text-warning-700',
  ALTERATION: 'bg-danger-50 text-danger-600',
  READY: 'bg-success-50 text-success-700',
  DELIVERED: 'bg-surface-sunken text-content-subtle',
};
// A solid colour per stage, for the progress dots in the order list. The
// tones above are tinted backgrounds for chips; a 7px dot needs full strength
// to be seen at all.
export const STAGE_DOT = {
  RECEIVED: 'bg-line-strong',
  CUTTING: 'bg-info-500',
  CUTTING_DONE: 'bg-info-500',
  ASSIGNED: 'bg-brand-400',
  STITCHING: 'bg-brand-500',
  QC: 'bg-warning-500',
  ALTERATION: 'bg-danger-500',
  READY: 'bg-success-500',
  DELIVERED: 'bg-success-600',
};

// The moves a person makes; everything else follows the workers on its own.
// This used to be a shorter list than the one the order list offered, so the
// same chip could be moved on one screen and was dead on another — "Order
// received" in particular had no moves at all. One table now, used by both.
export const HAND_MOVES = {
  RECEIVED: ['QC', 'READY'],
  CUTTING: ['QC', 'READY'],
  CUTTING_DONE: ['QC', 'READY'],
  ASSIGNED: ['QC', 'READY'],
  STITCHING: ['QC', 'READY'],
  QC: ['ALTERATION', 'READY'],
  ALTERATION: ['QC', 'READY'],
  READY: ['DELIVERED', 'ALTERATION'],
  DELIVERED: [],
};

/**
 * A garment's stage, and the button that moves it on.
 *
 * Only the moves the app cannot work out for itself are offered — checking,
 * alteration, ready, delivered. Cutting and stitching move themselves when the
 * worker starts or finishes.
 */
export default function StageControl({ item, invoiceId, compact = false, onMoved }) {
  const qc = useQueryClient();
  const { success, error } = useToast();
  const [open, setOpen] = useState(false);
  // The "Move" list is drawn in a portal: this control sits inside listings
  // that scroll sideways, and an in-flow panel gets clipped by them.
  const [box, setBox] = useState(null);
  const btnRef = useRef(null);
  const panelRef = useRef(null);
  const stage = item.stage || 'RECEIVED';
  const moves = HAND_MOVES[stage] || [];

  const place = useCallback(() => {
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    const width = Math.min(176, window.innerWidth - 16);
    const below = window.innerHeight - r.bottom - 8;
    const needed = (HAND_MOVES[item.stage || 'RECEIVED'] || []).length * 30 + 8;
    const up = below < needed && r.top > below;
    setBox({
      left: Math.min(Math.max(8, r.left), window.innerWidth - width - 8),
      width,
      top: up ? undefined : r.bottom + 4,
      bottom: up ? window.innerHeight - r.top + 4 : undefined,
    });
  }, [item.stage]);

  useEffect(() => {
    if (!open) return undefined;
    place();
    const away = (e) => {
      if (btnRef.current?.contains(e.target) || panelRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const on = () => place();
    document.addEventListener('mousedown', away);
    window.addEventListener('scroll', on, true);
    window.addEventListener('resize', on);
    return () => {
      document.removeEventListener('mousedown', away);
      window.removeEventListener('scroll', on, true);
      window.removeEventListener('resize', on);
    };
  }, [open, place]);

  const mut = useMutation({
    mutationFn: (next) => setItemStage(item.id, { stage: next }),
    onSuccess: (d) => {
      success(d.message || 'Moved.');
      setOpen(false);
      // The row is drawn from the order on screen, so tell it what changed.
      onMoved?.({ stage: d.stage, stageAt: d.stageAt, expectedAt: d.expectedAt, holder: d.holder });
      qc.invalidateQueries({ queryKey: ['invoice-timeline', invoiceId] });
      qc.invalidateQueries({ queryKey: ['invoice', String(invoiceId)] });
      qc.invalidateQueries({ queryKey: ['production-board'] });
    },
    onError: (e) => error(e.response?.data?.message || 'Could not move the garment.'),
  });

  const late = item.expectedAt && !['READY', 'DELIVERED'].includes(stage) && new Date(item.expectedAt) < new Date();

  return (
    <span className="relative inline-flex flex-wrap items-center gap-1.5">
      <span className={`rounded-full px-2 py-0.5 text-2xs font-bold uppercase tracking-wide ${STAGE_TONE[stage]}`}>
        {STAGE_LABEL[stage] || stage}
      </span>
      {item.holder && <span className="text-2xs text-content-subtle">with {item.holder}</span>}
      {item.expectedAt && !compact && (
        <span className={`text-2xs ${late ? 'font-bold text-danger-600' : 'text-content-subtle'}`}>
          due {fmtDate(item.expectedAt)}{late ? ' · late' : ''}
        </span>
      )}

      {moves.length > 0 && (
        <>
          <button
            ref={btnRef}
            type="button"
            className="inline-flex items-center gap-0.5 rounded border border-line px-1.5 py-0.5 text-2xs font-semibold text-brand-700 hover:bg-brand-50"
            onClick={() => { if (!open) place(); setOpen((o) => !o); }}
            disabled={mut.isPending}
          >
            Move <ChevronRight size={11} />
          </button>
          {open && box && createPortal(
            <span
              ref={panelRef}
              style={{ position: 'fixed', left: box.left, top: box.top, bottom: box.bottom, width: box.width }}
              className="z-[70] block overflow-hidden rounded-lg border border-line bg-surface shadow-pop"
            >
              {moves.map((m) => (
                <button
                  key={m}
                  type="button"
                  className="block w-full px-3 py-2 text-left text-xs hover:bg-surface-hover"
                  onClick={() => mut.mutate(m)}
                >
                  → {STAGE_LABEL[m]}
                </button>
              ))}
            </span>,
            document.body,
          )}
        </>
      )}
    </span>
  );
}
